import loadable from '@loadable/component';
import { type FunctionComponent } from 'react';
import { configureRoute } from '../../../utils/routes/configure-route';
import { FourOhFour } from '../../components/error-components/404/404.component';
import { ARTICLE_PREFIX, CATEGORY_PREFIX, PREFIXED_ID_ROUTE, PRODUCT_PREFIX } from '../../constants/links';
import { useRouteParams } from '../../hooks/routing/params.hook';

const LoadableProductRouter = loadable(
	() => import(/* webpackChunkName: "routedproductrouter" */ '../product/product-router/product-router.page'),
	{
		resolveComponent: ({ ProductRouter }) => ProductRouter
	}
);

const LoadableCategoryRouter = loadable(
	() => import(/* webpackChunkName: "routedcategoryrouter" */ '../category/category-router/category-router.page'),
	{
		resolveComponent: ({ CategoryRouter }) => CategoryRouter
	}
);

const LoadableArticlePage = loadable(() => import(/* webpackChunkName: "routedarticlepage" */ '../article/article.page'), {
	resolveComponent: ({ ArticlePage }) => ArticlePage
});

const prefixMap = {
	[CATEGORY_PREFIX]: (id: number) => <LoadableCategoryRouter categoryId={id} />,
	[PRODUCT_PREFIX]: (id: number) => <LoadableProductRouter familyId={id} />,
	[ARTICLE_PREFIX]: (id: number) => <LoadableArticlePage articleId={id} />
};
const isValidPrefix = (prefix: string): prefix is keyof typeof prefixMap => {
	return prefix in prefixMap;
};

/*
 * Router component that handles routes with prefixed IDs such as /c123, /p456, /a789 etc.
 */
export const PrefixedIdRouter: FunctionComponent = () => {
	const { prefixedId = '' } = useRouteParams<{ prefixedId: string }>();

	const prefix = prefixedId[0];
	const id = Number(prefixedId.slice(1));

	if (!isValidPrefix(prefix) || isNaN(id)) return <FourOhFour />;

	const renderer = prefixMap[prefix];
	return renderer(id);
};

export const PrefixedRouter = configureRoute({ path: PREFIXED_ID_ROUTE }, PrefixedIdRouter);
